import Cursor from '@studiometa/js-toolkit/components/Cursor';
import matrix from '@studiometa/js-toolkit/utils/css/matrix';

/**
 * @typedef {Object} SolutionsRef
 * @property {HTMLElement[]} links[]
 * @property {HTMLElement} cursor
 * @property {HTMLElement} mask
 * @property {HTMLElement} links_container
 * @property {HTMLElement} overlay
 */

/**
 * @typedef {Object} SolutionsInterface
 * @property {SolutionsRef} $refs
 * @property {Number} linkIndex
 * @property {Boolean} disabled
 */

/**
 * Homepage class.
 */
export default class Solutions extends Cursor {
  /**
   * Class config
   * @type {Object}
   */
  static config = {
    ...Cursor.config,
    name: 'Solutions',
    refs: ['links[]', 'cursor', 'mask', 'links_container', 'overlay'],
    options: {
      ...Cursor.config.options,
      translateDampFactor: {
        type: Number,
        default: 0.1,
      },
    },
  };

  /**
   * Initialize the component's behavior.
   *
   * @this {Solutions & SolutionsInterface}
   */
  mounted() {
    const imagesSrc = this.$refs.links.map((link) => link.dataset.src);
    this.linkIndex = -1;
    this.images = [];

    this.disabled = imagesSrc.includes('');

    if (this.disabled) {
      this.$el.classList.add('solutions--disabled');
      return;
    }

    imagesSrc.forEach((imageSrc) => {
      const image = new Image();
      image.src = imageSrc;

      this.images.push(image);
      this.$refs.cursor.appendChild(image);
    });

    this.$refs.links.forEach((link, index) => {
      const span = document.createElement('span');
      span.innerText = link.innerText;
      this.$refs.overlay.appendChild(span);

      link.addEventListener('mousemove', () => {
        this.linkIndex = index;
      });
      link.addEventListener('mouseleave', () => {
        this.linkIndex = -1;
      });
    });
  }

  /**
   * Render the cursor.
   *
   * @param  {Object} options
   * @param  {Number} options.x
   * @param  {Number} options.y
   * @param  {Number} options.scale
   * @this   {Solutions & SolutionsInterface}
   * @return {void}
   */
  render({ x, y }) {
    if (this.disabled) return;

    const transform = matrix({
      translateX: x,
      translateY: y + (window.scrollY - this.$el.offsetTop),
    });

    this.$refs.cursor.style.transform = `translateZ(0) ${transform}`;

    const targetImage = this.images[this.linkIndex];

    if (targetImage) {
      this.$refs.overlay.style.opacity = '1';

      const imageDimensions = {
        x: targetImage.naturalWidth,
        y: targetImage.naturalHeight,
      };

      const maskDimensions = {
        x:
          imageDimensions.x / imageDimensions.y > 1
            ? 400
            : (imageDimensions.x * 400) / imageDimensions.y,
        y:
          imageDimensions.x / imageDimensions.y > 1
            ? (imageDimensions.y * imageDimensions.x) / 400
            : 400,
      };

      const maskPosition = {
        x: x - this.$refs.links_container.getBoundingClientRect().left - 0.5 * maskDimensions.x,
        y: y - this.$refs.links_container.getBoundingClientRect().top - 0.5 * maskDimensions.y,
      };

      this.$refs.mask.setAttribute('x', maskPosition.x.toString());
      this.$refs.mask.setAttribute('y', maskPosition.y.toString());
      this.$refs.mask.setAttribute('width', maskDimensions.x.toString());
      this.$refs.mask.setAttribute('height', maskDimensions.y.toString());

      this.$refs.overlay.querySelectorAll('span').forEach((span, spanIndex) => {
        if (spanIndex === this.linkIndex) {
          span.style.opacity = '1';
        } else {
          span.style.opacity = '';
        }
      });

      // Force redraw to fix Safari display bug.
      this.$refs.overlay.classList.remove('solutions__overlay--clipped');
      this.$refs.overlay.offsetWidth; // eslint-disable-line
      this.$refs.overlay.classList.add('solutions__overlay--clipped');
    } else {
      this.$refs.overlay.style.opacity = '0';
    }

    if (this.linkIndex > -1) {
      if (!this.$refs.cursor.classList.contains('active')) {
        this.$refs.cursor.classList.add('active');
      }

      this.images.forEach((image, index) => {
        if (index === this.linkIndex) {
          image.style.opacity = 1;
        } else {
          image.style.opacity = 0;
        }
      });

      this.$refs.links.forEach((link, index) => {
        if (index === this.linkIndex) {
          if (!link.classList.contains('active')) {
            link.classList.add('active');
          }
        } else {
          link.classList.remove('active');
        }
      });
    } else {
      if (this.$refs.cursor.classList.contains('active')) {
        this.$refs.cursor.classList.remove('active');
      }

      this.$refs.links.forEach((link) => {
        link.classList.remove('active');
      });
    }
  }
}
